import { FC, useMemo, useState, useCallback, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import cx from "classnames"
import EmptyList from "@/components/EmptyList"
import AddOrganizationModal from "../AddOrganizationModal"
import EditOrganizationModal from "../EditOrganizationModal"
import AddIcon from "@/components/icons/AddIcon"
import OrganizationCard from "../OrganizationCard"
import { useGetOrganizationsQuery } from "@/redux/services/organizationApi"
import { useAuth } from "@/hooks/useAuth"
import SetOrganizationStatusModal from "../SetOrganizationStatusModal"
import { alphabetDateSortBy } from "../../utils"
import DeleteOrganizationModal from "../DeleteOrganizationModal"
import ErrorMessage from "@/components/ErrorMessage"
import Button from "@/components/Button"
import Loader from "@/components/Loader"
import { useOrganizationPermissions } from "@/hooks/useOrganizationPermissions"
import { message } from "antd"

type OrganizationModal = null | "edit" | "delete" | "set status"

const OrganizationsList: FC = () => {
  const [searchParams] = useSearchParams()
  const isActive = searchParams.get("isActive")
    ? searchParams.get("isActive") === "true"
    : "true"
  const navigate = useNavigate()
  const { data: orgs = [], isLoading, isError } = useGetOrganizationsQuery()
  const { user } = useAuth()
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<
    null | string
  >(null)
  const [openedModal, setOpenedModal] = useState<OrganizationModal>(null)
  const { accessiblePages } = useOrganizationPermissions()

  // Auto-redirect to the only organization if user has only one
  useEffect(() => {
    if (user?.isInboxingUser && !user?.isAdmin) return

    // Only redirect if data has loaded and user has exactly one organization that's active
    if (!isLoading && orgs.length === 1 && orgs[0].status === "enabled") {
      const org = orgs[0]

      // Prioritize dashboard and inbox, then fallback to other accessible pages
      let redirectPage = "dashboard"

      // First check if dashboard is accessible
      if (accessiblePages.includes("/dashboard")) {
        redirectPage = "dashboard"
      }
      // Then check if inbox is accessible
      else if (accessiblePages.includes("/inbox")) {
        redirectPage = "inbox"
      }
      // Otherwise use the first accessible page
      else {
        const firstAccessiblePage = accessiblePages
          .find((page) => page)
          ?.replace(/^\//, "")
        if (firstAccessiblePage) {
          redirectPage = firstAccessiblePage
        }
      }

      // Navigate to the organization's page
      navigate(`/organization/${org._id}/${redirectPage}`)
    }
  }, [
    orgs,
    isLoading,
    navigate,
    accessiblePages,
    user?.isInboxingUser,
    user?.isAdmin,
  ])

  const emptyMessage = isActive
    ? "There is no active organization."
    : "There is no inactive organization."
  const [openAddModal, setOpenAddModal] = useState(false)
  const [deletedId, setDeletedId] = useState("")

  const handleOpenAddModal = () => setOpenAddModal(true)
  const closeAddModal = () => setOpenAddModal(false)

  const orgsFiltered = useMemo(() => {
    if (!orgs.length) {
      return []
    }
    const searchedOrgsTerm = searchParams.get("term")
    let resultOrgs = isActive
      ? orgs.filter(({ status }) => status === "enabled")
      : orgs.filter(({ status }) => status === "disabled")
    if (searchedOrgsTerm) {
      resultOrgs = resultOrgs.filter(({ name }) =>
        name.trim().toLowerCase().includes(searchedOrgsTerm.toLowerCase()),
      )
    }
    const sortBy = searchParams.get("sortBy") ?? "Newest"
    if (sortBy) {
      resultOrgs = alphabetDateSortBy(sortBy, resultOrgs)
    }
    return resultOrgs
  }, [isActive, orgs, searchParams])

  const openOrganizationModal = (id: string) => {
    setSelectedOrganizationId(id)
  }

  const selectedOrganization = orgsFiltered.find(
    ({ _id }) => _id === selectedOrganizationId,
  )
  const hasInboxingDomainsAccess = useMemo(
    () =>
      accessiblePages.some((page) =>
        page.toLowerCase().includes("inboxing-domains"),
      ),
    [accessiblePages],
  )

  const closeModal = () => {
    setSelectedOrganizationId(null)
    setOpenedModal(null)
  }

  const onOrganizationModalOpen = (
    id: string,
    modalType: OrganizationModal,
  ) => {
    return () => {
      openOrganizationModal(id)
      setOpenedModal(modalType)
    }
  }

  const editModalOpened =
    selectedOrganizationId && selectedOrganization && openedModal === "edit"
  const setStatusModalOpened =
    selectedOrganization &&
    selectedOrganizationId &&
    openedModal === "set status"

  const navigateToFirstAccessiblePage = useCallback(
    (_id: string) => {
      if (!_id) return

      if (user?.isInboxingUser && !user?.isAdmin && hasInboxingDomainsAccess) {
        navigate(`/organization/${_id}/inboxing-domains`)
        return
      }

      const priorityRoutes = [
        {
          path: "/dashboard",
          route: `/organization/${_id}/dashboard?indicators=all`,
        },
        { path: "/campaigns", route: `/organization/${_id}/campaigns` },
        { path: "/prospects", route: `/organization/${_id}/prospects` },
        { path: "/dnc", route: `/organization/${_id}/dnc` },
        { path: "/inbox", route: `/organization/${_id}/inbox` },
        { path: "/sequences", route: `/organization/${_id}/sequences` },
        { path: "/master-lists", route: `/organization/${_id}/master-lists` },
        {
          path: "/search-criteria",
          route: `/organization/${_id}/search-criteria`,
        },
        { path: "/domains", route: `/organization/${_id}/domains` },
        { path: "/emails", route: `/organization/${_id}/emails` },
        { path: "/senders", route: `/organization/${_id}/senders` },
      ]

      // Special case for dashboard (most common page)
      const dashboardVariations = ["/dashboard", "dashboard", "Dashboard"]
      const hasDashboardAccess = dashboardVariations.some((v) =>
        accessiblePages.includes(v),
      )

      if (hasDashboardAccess) {
        navigate(`/organization/${_id}/dashboard?indicators=all`)
        return
      }

      // Enhanced path matching with variations
      const findAccessiblePage = (path: string) => {
        const variations = [
          path,
          path.toLowerCase(),
          path.substring(1), // Remove leading slash
          path.substring(1).toLowerCase(),
          path.charAt(1).toUpperCase() + path.substring(2), // Capitalize first letter after slash
        ]

        return variations.some((v) =>
          accessiblePages.some((p) => p.includes(v)),
        )
      }

      const firstAccessible = priorityRoutes.find((route) =>
        findAccessiblePage(route.path),
      )

      if (firstAccessible) {
        navigate(firstAccessible.route)
      } else {
        // If no accessible pages found, stay on the organizations page
        message.warning(
          "This organization has no accessible pages for your user role. Please contact an administrator.",
        )
      }
    },
    [
      accessiblePages,
      hasInboxingDomainsAccess,
      navigate,
      user?.isAdmin,
      user?.isInboxingUser,
    ],
  )

  if (isError) {
    return <ErrorMessage />
  }

  return (
    <div
      className={cx("grid gap-5 mt-4 flex-1", {
        "grid-cols-1": !orgsFiltered.length,
        "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4": orgsFiltered.length,
        "place-items-center min-h-[300px]": !orgs.length,
      })}
    >
      {isLoading ? (
        <Loader />
      ) : orgsFiltered.length ? (
        <>
          {orgsFiltered.map(({ name, status, _id, isMaster }) => (
            <OrganizationCard
              key={_id}
              name={name}
              enabled={status === "enabled"}
              isMaster={isMaster}
              footerName={`${user?.firstName} ${user?.lastName}`}
              onEdit={onOrganizationModalOpen(_id, "edit")}
              onDelete={() => setDeletedId(_id)}
              onChangeStatus={onOrganizationModalOpen(_id, "set status")}
              onClick={() => navigateToFirstAccessiblePage(_id)}
            />
          ))}
        </>
      ) : (
        <EmptyList message={emptyMessage} className="mt-[88px]">
          {isActive && (
            <Button
              onClick={handleOpenAddModal}
              className="btn-sm group bg-white hover:bg-primary border !border-solid border-outline-dark hover:border-primary"
            >
              <AddIcon className="transition-colors text-primary group-hover:text-white" />
              <span className="transition-colors font-medium text-primary group-hover:text-white">
                Add New Organization
              </span>
            </Button>
          )}
        </EmptyList>
      )}
      <AddOrganizationModal open={openAddModal} onClose={closeAddModal} />
      {editModalOpened && (
        <EditOrganizationModal
          organizationId={selectedOrganizationId}
          initialName={selectedOrganization.name}
          initialUrl={selectedOrganization.url}
          initialCatchAll={selectedOrganization.catchAll}
          open={!!selectedOrganizationId}
          onClose={closeModal}
        />
      )}
      {setStatusModalOpened && (
        <SetOrganizationStatusModal
          currentStatus={selectedOrganization.status}
          organizationId={selectedOrganizationId}
          initialName={selectedOrganization.name}
          open={!!selectedOrganizationId}
          onClose={closeModal}
        />
      )}
      <DeleteOrganizationModal
        data={orgs.find(({ _id }) => _id === deletedId)}
        open={!!deletedId}
        onClose={() => setDeletedId("")}
      />
    </div>
  )
}

export default OrganizationsList
